import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, ArrowRight, Check, Truck, FileText, BadgeDollarSign,
  ShieldCheck, CalendarClock, PencilLine, PhoneCall, Star,
} from "lucide-react";
import { CONTACT, IMAGES, DUMPSTER_DETAILS } from "@/lib/data";
import { useContent } from "@/i18n/content";
import { useI18n } from "@/i18n";
import { Eyebrow, SectionHeading, BottomWave, reveal } from "@/components/site/primitives";
import Seo from "@/components/site/Seo";
import FaqAccordion from "@/components/site/FaqAccordion";
import ServiceAreasSeo from "@/components/site/ServiceAreasSeo";

const FEATURE_ICONS = [Truck, FileText, BadgeDollarSign];
const STEP_ICONS = [PhoneCall, CalendarClock, PencilLine];

export default function BusinessWaste() {
  const { t, localePath } = useI18n();
  const {
    STATS, TRUST_BADGES, BUSINESS_INTRO, BUSINESS_DUMPSTERS_INTRO,
    BUSINESS_FEATURES, SERVICE_GUARANTEE, DUMPSTER_SIZES, PROCESS_STEPS,
    BUSINESS_ANY_INTRO, BUSINESS_TYPES, WEEKLY_INTRO, WEEKLY_FREQUENCIES, BUSINESS_WASTE_FAQS,
  } = useContent();
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: BUSINESS_WASTE_FAQS.map((f) => ({
      "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <>
      <Seo
        title={t("bw.seoTitle")}
        description={t("bw.seoDesc")}
        path="/business-waste-service"
        image={IMAGES.frontloadDumpster}
        jsonLd={faqJsonLd}
      />
      {/* HERO */}
      <section className="relative bg-[#0E3B3B] text-white pt-32 pb-32 overflow-hidden" data-testid="bw-hero">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 85% 15%, rgba(16,185,129,0.55), transparent 45%)" }} />
        <div className="relative z-[2] max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-emerald-300/80 mb-4">
              <Link to={localePath("/")} className="hover:text-emerald-200">{t("nav.home")}</Link>
              <span className="mx-2 text-white/30">/</span><span className="text-white/70">{t("bw.crumb")}</span>
            </div>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
              {t("bw.h1a")} <span className="text-emerald-400">{t("bw.h1b")}</span>
            </h1>
            <p className="mt-5 font-display font-bold text-2xl sm:text-3xl text-emerald-300">{t("bw.save")}</p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={localePath("/dumpster-rentals")} className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/25 text-white font-semibold hover:border-emerald-400 hover:text-emerald-300 transition-all" data-testid="bw-rolloff-btn">
                {t("bw.rolloffBtn")}
              </Link>
              <Link to={localePath("/quick-quote")} className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 active:scale-95 transition-all" data-testid="bw-quote-btn">
                {t("cta.requestQuote")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-8">
              {STATS.slice(0, 2).map((s, i) => (
                <div key={s.label}>
                  <div className="font-display font-extrabold text-4xl">{s.metric}</div>
                  <div className="text-white/70 text-sm max-w-[160px]">{t(`stat.${i}`)}</div>
                </div>
              ))}
              <div className="flex flex-col gap-2">
                {TRUST_BADGES.map((b) => (
                  <span key={b} className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-wider text-white/70">
                    <Star size={12} className="text-emerald-400 fill-emerald-400" /> {b}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="relative">
            <div className="bg-[#F0EFEA] rounded-3xl p-6 shadow-2xl max-w-md mx-auto">
              <img loading="lazy" decoding="async" src={IMAGES.frontloadDumpster} alt="EWS front-load commercial dumpster" className="w-full rounded-xl" />
            </div>
          </motion.div>
        </div>
        <BottomWave />
      </section>

      {/* Switch banner */}
      <div className="bg-[#0B0F14] py-4 text-center">
        <p className="font-display font-bold text-white text-lg sm:text-2xl tracking-tight">{t("bw.switchBanner")}</p>
      </div>

      {/* Intro */}
      <section className="relative bg-[#FBFAF8] py-24 overflow-hidden">
        <span className="pointer-events-none select-none absolute inset-x-0 top-6 text-center font-display font-extrabold text-[70px] sm:text-[130px] text-black/[0.03] leading-none">Waste Service</span>
        <div className="relative z-[2] max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <Eyebrow>{t("bw.introEyebrow")}</Eyebrow>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B0F14]">{t("bw.introTitle")}</h2>
          <p className="mt-6 text-slate-600 leading-relaxed">{BUSINESS_INTRO}</p>
        </div>
      </section>

      {/* AFFORDABLE teal band */}
      <div className="relative bg-[#0E3B3B] py-16 overflow-hidden">
        <h2 className="relative z-[2] text-center font-display font-extrabold tracking-[0.05em] text-white text-4xl sm:text-6xl uppercase">{t("bw.affordable")}</h2>
      </div>

      {/* Dumpsters for all businesses */}
      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
              <SectionHeading eyebrow={t("bw.dumpsterEyebrow")} title={t("bw.dumpsterTitle")} />
              <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">{BUSINESS_DUMPSTERS_INTRO}</p>
            </motion.div>
            <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative">
              <div className="bg-[#F0EFEA] rounded-3xl p-6 max-w-sm mx-auto shadow-soft">
                <img loading="lazy" decoding="async" src={IMAGES.frontloadDumpster} alt="Front-load dumpster dimensions" className="w-full rounded-xl" />
              </div>
              <p className="text-center font-mono-tech text-xs uppercase tracking-widest text-emerald-700 mt-3">{t("bw.frontloadCaption")}</p>
            </motion.div>
          </div>

          {/* Feature cards */}
          <div className="mt-16 grid sm:grid-cols-3 gap-6">
            {BUSINESS_FEATURES.map((f, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <motion.div key={f.title} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
                  className="bg-white border border-[#E7E5DF] rounded-2xl p-7 shadow-soft">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-emerald-600/10 text-emerald-700 mb-4"><Icon size={22} /></span>
                  <h3 className="font-display font-bold text-lg text-[#0B0F14]">{f.title}</h3>
                  <p className="mt-1.5 text-slate-600 text-sm">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Guarantee */}
          <div className="mt-8 flex items-start gap-4 bg-[#0E3B3B] text-white rounded-2xl p-7 sm:p-9">
            <ShieldCheck size={30} className="text-emerald-400 shrink-0 mt-1" />
            <div>
              <h3 className="font-display font-bold text-lg mb-1">{t("bw.guaranteeTitle")}</h3>
              <p className="text-white/80 text-sm leading-relaxed max-w-3xl">{SERVICE_GUARANTEE}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dumpster sizes */}
      <section className="relative bg-[#F0EFEA] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("bw.sizesEyebrow")} title={t("bw.sizesTitle")} highlight={t("bw.sizesHighlight")} className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DUMPSTER_SIZES.map((d, i) => (
              <motion.div key={d.size} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
                className="bg-white border border-[#E7E5DF] rounded-3xl p-7 shadow-soft flex flex-col hover:-translate-y-1 hover:border-emerald-600/40 transition-[transform,border-color,box-shadow] duration-300 transform-gpu" data-testid={`dumpster-${i}`}>
                <Link to={localePath(`/business-waste-service/${DUMPSTER_DETAILS[i].slug}`)} className="flex flex-col flex-1">
                  <div className="mb-5 grid place-items-center h-48">
                    <img loading="lazy" decoding="async" src={d.img} alt={d.size} className="max-h-48 w-auto object-contain" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-emerald-700">{d.size}</h3>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed flex-1">{d.desc}</p>
                  <p className="mt-4 font-semibold text-[#0B0F14] text-sm">{d.bags}</p>
                  <p className="text-slate-500 text-sm">{d.dims}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 font-semibold text-[#0B0F14] group-hover:text-emerald-700">
                    {t("bw.viewDetails")} <ArrowRight size={16} />
                  </span>
                </Link>
              </motion.div>
            ))}
            <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="bg-[#0E3B3B] text-white rounded-3xl p-8 flex flex-col justify-center">
              <h3 className="font-display font-bold text-2xl">{t("bw.notSureTitle")}</h3>
              <p className="mt-3 text-white/75 text-sm">{t("bw.notSureDesc")}</p>
              <a href={CONTACT.phoneHref} className="mt-6 inline-flex items-center gap-2 font-bold text-emerald-300 hover:text-emerald-200"><Phone size={18} /> {CONTACT.phone}</a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("bw.processEyebrow")} title={t("bw.processTitle")} highlight={t("bw.processHighlight")} className="mb-14" />
          <div className="grid md:grid-cols-3 gap-6">
            {PROCESS_STEPS.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <motion.div key={s.step} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
                  className="relative bg-white border border-[#E7E5DF] rounded-3xl p-8 shadow-soft">
                  <span className="absolute top-6 right-7 font-display font-extrabold text-5xl text-[#F0EFEA]">{s.step}</span>
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#0E3B3B] text-emerald-300 mb-5"><Icon size={24} /></span>
                  <h3 className="font-display font-bold text-lg text-[#0B0F14] max-w-[240px]">{s.title}</h3>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commercial weekly pickup schedules — targets "weekly business trash pickup Miami-Dade" */}
      <section className="relative bg-[#F0EFEA] py-24" data-testid="weekly-service">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
            <div>
              <SectionHeading eyebrow={t("bw.weeklyEyebrow")} title={t("bw.weeklyTitle")} highlight={t("bw.weeklyHighlight")} />
              <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">{WEEKLY_INTRO}</p>
              <a href={CONTACT.phoneHref} className="mt-7 inline-flex items-center gap-2 font-bold text-emerald-700 hover:text-emerald-800" data-testid="weekly-cta"><Phone size={18} /> {t("bw.weeklyCta")} {CONTACT.phone}</a>
            </div>
            <div className="space-y-3">
              {WEEKLY_FREQUENCIES.map((w, i) => (
                <motion.div key={w.freq} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
                  className="flex items-center gap-4 bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft" data-testid={`weekly-freq-${i}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#0E3B3B] text-emerald-300 shrink-0"><CalendarClock size={22} /></span>
                  <div>
                    <div className="font-display font-extrabold text-lg text-[#0B0F14]">{w.freq}</div>
                    <div className="text-slate-600 text-sm">{w.for}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Waste service for any business */}
      <section className="relative bg-[#FBFAF8] pb-24 overflow-hidden">
        <div className="relative bg-[#0E3B3B] py-16">
          <h2 className="text-center font-display font-extrabold tracking-tight text-white text-3xl sm:text-5xl uppercase px-5">{t("bw.anyBusiness")}</h2>
        </div>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 mt-12 text-center">
          <p className="text-slate-600 leading-relaxed">{BUSINESS_ANY_INTRO}</p>
        </div>
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 mt-12">
          <span className="pointer-events-none select-none absolute inset-x-0 top-2 text-center font-display font-extrabold text-[60px] sm:text-[110px] text-black/[0.03] leading-none">Collection</span>
          <div className="relative z-[2] grid sm:grid-cols-2 gap-x-12 gap-y-4">
            {BUSINESS_TYPES.map((t) => (
              <div key={t} className="flex items-center gap-3 text-[#0B0F14]">
                <span className="grid place-items-center w-6 h-6 rounded-full bg-emerald-600/10 text-emerald-700"><Check size={14} strokeWidth={3} /></span>
                <span className="font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — progressive disclosure, targets commercial waste long-tail */}
      <FaqAccordion
        eyebrow={t("bw.faqEyebrow")}
        title={t("bw.faqTitle")}
        highlight={t("bw.faqHighlight")}
        faqs={BUSINESS_WASTE_FAQS}
      />

      {/* Subtle local SEO */}
      <ServiceAreasSeo service="commercial business waste service, front-load dumpster rentals and recycling" />

      {/* CTA */}
      <CTABand />
    </>
  );
}

export function CTABand() {
  const { t, localePath } = useI18n();
  return (
    <section className="relative bg-[#0B0F14] border-t-4 border-emerald-500 py-16" data-testid="cta-band">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col lg:flex-row lg:items-center justify-between gap-7">
        <div className="max-w-2xl">
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-bold" data-testid="cta-eyebrow">{t("cta.localEyebrow")}</p>
          <h2 className="mt-2 font-display font-extrabold text-white text-2xl sm:text-3xl tracking-tight">{t("cta.ready")}</h2>
          <p className="mt-2 text-white/60 text-sm leading-relaxed">{t("cta.localBody")}</p>
        </div>
        <div className="flex flex-wrap gap-4">
          <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:border-emerald-400 hover:text-emerald-300 transition-all" data-testid="cta-call-btn"><Phone size={18} /> {CONTACT.phone}</a>
          <Link to={localePath("/quick-quote")} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 transition-all" data-testid="cta-quote-btn">{t("cta.requestQuote")} <ArrowRight size={18} /></Link>
        </div>
      </div>
    </section>
  );
}
